import { supabase } from '@config/supabase';

const RETRYABLE_AUTH_STATUSES = new Set([401, 403]);

export class SessionExpiredError extends Error {
  constructor(message: string = 'Session expired. Please sign in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

const getAccessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;

  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data.session?.access_token) {
    throw new SessionExpiredError('Invalid or expired session');
  }
  return data.session.access_token;
};

const mergeHeaders = (init?: RequestInit, token?: string): Headers => {
  const headers = new Headers(init?.headers ?? {});
  if (!headers.has('Content-Type') && init?.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

export const fetchWithSupabaseAuth = async (
  input: string | URL,
  init?: RequestInit
): Promise<Response> => {
  const firstToken = await getAccessToken();
  let response = await fetch(input, {
    ...init,
    headers: mergeHeaders(init, firstToken),
  });

  if (!RETRYABLE_AUTH_STATUSES.has(response.status)) {
    return response;
  }

  const { data, error } = await supabase.auth.refreshSession();
  const refreshedToken = data.session?.access_token;
  if (error || !refreshedToken) {
    throw new SessionExpiredError('Invalid or expired session');
  }

  response = await fetch(input, {
    ...init,
    headers: mergeHeaders(init, refreshedToken),
  });

  if (RETRYABLE_AUTH_STATUSES.has(response.status)) {
    throw new SessionExpiredError('Invalid or expired session');
  }

  return response;
};
